// /**
//  * Orders details - list
//  */

// React
import React, {useContext} from 'react';

// UI
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {mdiChevronDown, mdiChevronUp, mdiTableColumn} from '@mdi/js';

// Helper
import {translate, checkMatchMedia} from '../../utils/helpers';

// Type
import {Item} from '../orders/interfaces';
import AuthContext from '../../authContext';
import theme from '../../theme';

type Data = Item;
type Props = {
  data: any;
  showItems: () => void;
};
type Ord = 'asc' | 'desc';
interface HeadCell {
  id: keyof Data;
  label: string;
}
interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      textAlign: 'left',
      padding: '10px 32px',
      backgroundColor: '#D5DADD !important',
      color: '#212121',
      '@media (max-width: 768px)': {
        fontSize: 10,
      },
    },
    body: {
      textAlign: 'left',
      padding: '15px 32px 25px 32px',
      fontSize: 14,
    },
  })
)(TableCell);

const StyledTableRow = withStyles((theme: Theme) =>
  createStyles({
    root: {
      '&:nth-of-type(even)': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    body: {
      fontSize: 14,
    },
  })
)(TableRow);
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
    },
    paper: {
      width: '100%',
      minWidth: '500px',
    },
    table: {
      // minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    textArea: {
      width: '100%',
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
    container: {
      maxHeight: 540,
      borderRadius: '4px',
      minWidth: '500px',
    },
    // Fully visible for active icons
    activeSortIcon: {
      opacity: 1,
      color: `${theme.palette.secondary.main}!important`,
    },
    // Half visible for inactive icons
    inactiveSortIcon: {
      opacity: 0.4,
    },
    // Cancel Dialog
    cancelIcon: {
      textAlign: 'center',
      position: 'relative',
      paddingLeft: theme.spacing(8),
      marginBottom: theme.spacing(7),
      '&::before': {
        content: '""',
        position: 'absolute',
        width: '100px',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%,-50%)',
        height: '100px',
        borderRadius: '50%',
        opacity: '0.2',
        backgroundColor: theme.palette.error.main,
      },
    },
    cancelTitle: {
      fontSize: '16px',
      fontWeight: 500,
      textAlign: 'center',
      marginBottom: theme.spacing(3),
    },
    actionsBox: {
      paddingTop: theme.spacing(2),
      paddingRight: theme.spacing(3),
      paddingLeft: theme.spacing(3),
    },
  })
);

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Ord,
  orderBy: Key
): (
  a: {[key in Key]: number | string},
  b: {[key in Key]: number | string}
) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const classes = useStyles();
  const {order, orderBy, onRequestSort} = props;
  const createSortHandler =
    (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };
  let headCells: HeadCell[] = [
    {id: 'productName', label: 'نام کالا'},
    {id: 'variantId', label: 'شناسه تنوع کالا'},
    {id: 'count', label: 'تعداد'},
    {id: 'price', label: 'قیمت'},
  ];

  return (
    <TableHead>
      <StyledTableRow>
        {headCells.map(headCell => (
          <StyledTableCell
            key={headCell.id}
            align="center"
            sortDirection={orderBy === headCell.id ? order : false}
            data-cy={headCell.id}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
              classes={{
                // Override with the active class if this is the selected column or inactive otherwise
                icon:
                  orderBy === headCell.id
                    ? classes.activeSortIcon
                    : classes.inactiveSortIcon,
              }}
              IconComponent={() => (
                <Icon
                  path={
                    orderBy === headCell.id && order === 'desc'
                      ? mdiChevronDown
                      : mdiChevronUp
                  }
                  size={0.7}
                  className={
                    orderBy === headCell.id
                      ? classes.activeSortIcon
                      : classes.inactiveSortIcon
                  }
                />
              )}
            >
              {headCell.label}
            </TableSortLabel>
          </StyledTableCell>
        ))}
        <StyledTableCell />
      </StyledTableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const authContext = useContext(AuthContext);
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<keyof Data>('productId');

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <TableContainer className={classes.container}>
          <Table
            stickyHeader
            className={classes.table}
            aria-labelledby="tableTitle"
            size={checkMatchMedia('(max-width: 1024px)') ? 'small' : 'medium'}
            aria-label="enhanced table"
          >
            <EnhancedTableHead
              classes={classes}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={props.data?.length}
            />

            <TableBody>
              {stableSort(props.data, getComparator(order, orderBy))
                .slice()
                .map((row: any, index: number) => {
                  const labelId = `enhanced-table-checkbox-${index}`;
                  let variantName =
                    row.type === 'PRODUCT'
                      ? row.productName
                      : translate(row.type, 'inventory');
                  // for (let i = 0; i <= 2 && row.featureAndValues[i]; i++) {
                  //   variantName = variantName.concat(
                  //     ' - ',
                  //     row.featureAndValues[i].key,
                  //     ' : ',
                  //     row.featureAndValues[i].value
                  //   );
                  // }

                  // if (row.sellerName || row.providerName) {
                  //   const sellerName = row.sellerName ?? row.providerName;
                  //   variantName = variantName.concat(' ( ', sellerName, ' )');
                  // }

                  return (
                    <StyledTableRow
                      style={{
                        backgroundColor:
                          row.orderableStatus !== 'AVAILABLE'
                            ? '#FFFF8F'
                            : 'white',
                      }}
                      key={index}
                      hover
                      tabIndex={-1}
                    >
                      <StyledTableCell id={labelId} scope="row" align="center">
                        {row.productName}
                      </StyledTableCell>
                      <StyledTableCell align="center" data-cy="productCode">
                        {row.productCode}
                      </StyledTableCell>
                      {/* <StyledTableCell align="center" data-cy="variantCode">
                        {row.variantCode}
                      </StyledTableCell> */}
                      <StyledTableCell align="center" data-cy="amount">
                        {row.count}
                      </StyledTableCell>
                      <StyledTableCell align="center" data-cy="variantId">
                        {row.price}
                      </StyledTableCell>
                      <StyledTableCell align="left">
                        <IconButton
                          size="small"
                          title="مشاهده آیتم ها"
                          onClick={() => props.showItems(row)}
                          data-cy="showItem"
                        >
                          <Icon
                            path={mdiTableColumn}
                            size={1}
                            color={theme.palette.green.main}
                          />
                        </IconButton>
                      </StyledTableCell>
                    </StyledTableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}
